import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import {
    type AuditFilterChange,
    type AuditLogEntry,
    countByRequestId,
    sharedRequestCount,
} from '@/domain/auditLog';

import { AUDIT_COLUMNS, type AuditColumnId } from './auditLogColumns';
import {
    ACCENT_CLASS,
    type AuditLogRowData,
    renderAuditCell,
    selectUnlessCopying,
} from './auditLogRowCells';

import styles from './AuditLogTable.module.scss';

// Two lines per row, and still a 44 px target for the time button.
const ROW_HEIGHT = 56;

interface AuditLogTableProps {
    entries: readonly AuditLogEntry[];
    /** The visible columns, in table order. */
    columns: readonly AuditColumnId[];
    onSelect: (entry: AuditLogEntry) => void;
    onFilter: (change: AuditFilterChange) => void;
}

/**
 * Not sortable: rows arrive newest first from a keyset cursor, and sorting only the
 * loaded pages would misrepresent the log.
 */
export default function AuditLogTable({
    entries,
    columns,
    onSelect,
    onFilter,
}: AuditLogTableProps) {
    const { t } = useTranslation();
    const rows = useMemo((): AuditLogRowData[] => {
        const requestCounts = countByRequestId(entries);
        return entries.map((entry) => ({
            entry,
            requestCount: sharedRequestCount(requestCounts, entry),
        }));
    }, [entries]);

    const tableColumns = useMemo(
        (): DataTableColumnDef<AuditLogRowData>[] =>
            columns.map((id, index) => {
                const header = t(AUDIT_COLUMNS[id].headerKey);
                return {
                    id,
                    header:
                        id === 'time'
                            ? () => <span className={styles.timeHeader}>{header}</span>
                            : header,
                    cell: ({ row }) => renderAuditCell(id, row.original, { onSelect, onFilter }),
                    meta: {
                        width: AUDIT_COLUMNS[id].track,
                        cellClassName:
                            index === 0
                                ? (row) => ACCENT_CLASS[row.original.entry.outcome]
                                : undefined,
                    },
                };
            }),
        [columns, t, onSelect, onFilter]
    );

    return (
        <DataTable
            data={rows}
            columns={tableColumns}
            getRowId={(row) => String(row.entry.id)}
            layout="grid"
            rowHeight={ROW_HEIGHT}
            clampCells
            enableSorting={false}
            getRowProps={(row) => ({
                className: styles.row,
                onClick: () => selectUnlessCopying(row.original.entry, onSelect),
            })}
        />
    );
}
